import { BreakpointObserver } from '@angular/cdk/layout';
import { isPlatformBrowser } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Inject,
  LOCALE_ID,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { Meta } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { listAnimation } from '@animations/list';
import { skeletonEnter } from '@animations/skeleton-enter';
import { socialAnimation } from '@animations/social';
import { Company, Job } from '@ats/models';
import { SocialNetwork } from '@ats/models/lib/recruiter-api/company/company';
import { environment } from '@env/environment';
import { ScrollContentItemDirective } from '@features/home/home/directives/scroll-content-item.directive';
import { ScrollMenuItemDirective } from '@features/home/home/directives/scroll-menu-item.directive';
import { Metas } from '@features/home/home/seo-vars';
import { LocaleEnum } from '@models/commons/locales.enum';
import { Select, Store } from '@ngxs/store';
import { CompaniesState } from '@store/companies/companies.state';
import { FindJobs, InitStaticData } from '@store/jobs/jobs.actions';
import { JobsState, NB_ITEMS_TO_DISPLAY } from '@store/jobs/jobs.state';
import { WINDOW } from '@wizbii/angular-utilities';
import { combineLatest, fromEvent, Observable, Subject, Subscription } from 'rxjs';
import {
  distinctUntilChanged,
  filter,
  map,
  shareReplay,
  startWith,
  switchMap,
  takeUntil,
  tap,
  throttleTime,
} from 'rxjs/operators';

@Component({
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [skeletonEnter(), listAnimation, socialAnimation()],
})
export class HomeComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('navBar', { static: false }) navBar: ElementRef<HTMLElement>;

  @ViewChildren(ScrollMenuItemDirective) menu: QueryList<ScrollMenuItemDirective>;
  @ViewChildren(ScrollContentItemDirective) content: QueryList<ScrollContentItemDirective>;

  menuItems: ScrollMenuItemDirective[] = [];
  contentItems: ScrollContentItemDirective[] = [];

  private scrollSub: Subscription;

  @Select(CompaniesState.authenticatedCompany)
  company$: Observable<Company>;

  jobs$: Observable<Job[]>;
  currentPage$: Observable<number>;

  nbItemsToDisplay = NB_ITEMS_TO_DISPLAY;

  private readonly destroyed$ = new Subject<void>();
  private metaElements: HTMLMetaElement[] = [];

  constructor(
    private readonly breakpointObserver: BreakpointObserver,
    @Inject(WINDOW) private readonly window: Window,
    @Inject(PLATFORM_ID) private readonly platformId: Object,
    @Inject(LOCALE_ID) private readonly localeId: string,
    private readonly store: Store,
    private readonly metaService: Meta,
    readonly route: ActivatedRoute
  ) {
    this.currentPage$ = route.queryParamMap.pipe(
      map(filterMap => (filterMap.has('page') ? Number(filterMap.get('page')) : 1)),
      distinctUntilChanged(),
      shareReplay()
    );

    this.jobs$ = combineLatest([
      this.currentPage$,
      this.company$.pipe(
        filter(company => !!company),
        map(company => company._id),
        distinctUntilChanged()
      ),
    ]).pipe(
      tap(([_, companyId]) => store.dispatch(new FindJobs(companyId))),
      switchMap(([page, companyId]) => store.select(JobsState.list(companyId, page))),
      shareReplay()
    );
  }

  ngOnInit(): void {
    this.store.dispatch(new InitStaticData());

    this.company$.pipe(filter(company => !!company)).subscribe(company => {
      this.cleanSeo();

      this.metaElements = [
        this.metaService.addTag({
          name: 'author',
          content: company.name,
        }),
        this.metaService.addTag({
          itemprop: 'name',
          content: company.name,
        }),
        this.metaService.addTag({
          property: 'og:url',
          content: `https://${company._id}.atsii.fr`,
        }),
        this.metaService.addTag({
          property: 'og:image',
          content: `${environment.api.googleStorage}/${environment.wizbiiFiles}/${company.logo}`,
        }),
        this.metaService.addTag({
          property: 'og:site_name',
          content: `${company._id}.atsii.fr`,
        }),
        this.metaService.addTag({
          property: 'og:locale',
          content: LocaleEnum[this.localeId],
        }),
        this.metaService.addTag({
          property: 'profile:username',
          content: company.name,
        }),
        this.metaService.addTag({
          itemprop: 'image',
          content: `${environment.api.googleStorage}/${environment.wizbiiFiles}/${company.logo}`,
        }),
        ...this.metaService.addTags(Metas),
      ];
    });
  }

  ngAfterViewInit(): void {
    this.initBreakpointObserver();
  }

  private initBreakpointObserver() {
    if (!isPlatformBrowser(this.platformId) || !this.navBar || !this.content || !this.menuItems) {
      return;
    }

    this.contentItems = this.content.toArray(); // https://github.com/angular/angular/issues/17414
    this.menuItems = this.menu.toArray(); // https://github.com/angular/angular/issues/17414

    this.breakpointObserver
      .observe('(min-width: .0625em)')
      .pipe(takeUntil(this.destroyed$))
      .subscribe(event => {
        if (event.matches) {
          this.scrollSub = fromEvent(this.window, 'scroll', { passive: true })
            .pipe(
              takeUntil(this.destroyed$),
              startWith(null),
              throttleTime(50, undefined, { leading: true, trailing: true })
            )
            .subscribe(() => {
              this.updateMenuItems();
            });
        } else if (this.scrollSub) {
          this.scrollSub.unsubscribe();
        }
      });
  }

  private cleanSeo() {
    this.metaElements.forEach(meta => {
      this.metaService.removeTagElement(meta);
    });
    this.metaElements = [];
  }

  private updateMenuItems() {
    // Get the position of the center of the `navBar box` from the top
    const navBarPos =
      this.navBar.nativeElement.getBoundingClientRect().bottom - this.navBar.nativeElement.clientHeight / 2;

    const closestItem = this.contentItems.reduce((closest, next) => {
      // Compare the previous bottom value to the next top value
      return Math.abs(closest.rect.bottom - navBarPos) > Math.abs(next.rect.top - navBarPos) ? next : closest;
    }, this.contentItems[0]);

    // Add the active class to the menu-item who have the same index as the closest content-item
    this.menuItems.forEach((item, index) => {
      item.toggleClass(closestItem === this.contentItems[index]);
    });
  }

  trackByMedia(_, media: SocialNetwork) {
    return media.name;
  }

  ngOnDestroy(): void {
    this.cleanSeo();

    this.destroyed$.next();
    this.destroyed$.complete();
  }
}
